
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import NepaliDate from "nepali-date-converter";

import {FILE_BASE_URL} from "../../../../../constants/ApiConstant";
import {CONFIG} from "../../constants/BellStatusConstants";
import {fetchOverallBellStatus, fetchSchoolQueue} from "../../services/BellStatusService";
import {
    PALETTE,
    buildDrawer,
    categoryLook,
    fmtClock,
    filterCounts,
    filterSort,
    segColor,
} from "../../utils/overallUtils";
import OverallStyles from "../../components/OverallStyles";

function logoUrl(raw) {
    if (!raw) return null;
    return String(raw).startsWith("http") ? raw : `${FILE_BASE_URL}/${String(raw).replace(/^\/+/, "")}`;
}

/* ── small presentational pieces ───────────────────────────────────────────── */

function Logo({src, initials, bg, size = 52, radius = 14, font = 19}) {
    const [broken, setBroken] = useState(false);
    const url = logoUrl(src);
    const box = {
        width: size, height: size, flex: `0 0 ${size}px`, borderRadius: radius,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: font,
        color: "#FFFFFF", background: bg, overflow: "hidden",
    };
    if (url && !broken) {
        return (
            <div style={box}>
                <img src={url} alt="" onError={() => setBroken(true)} style={{width: "100%", height: "100%", objectFit: "cover"}} />
            </div>
        );
    }
    return <div style={box}>{initials}</div>;
}

function SummaryCard({label, value, sub, valueColor, tone}) {
    const warn = tone === "warn";
    return (
        <div style={{
            background: warn ? "#FFF6E5" : "#FFFFFF", borderRadius: 16, padding: "18px 20px",
            border: `1px solid ${warn ? "#F5D9A3" : "#E1E7F0"}`, display: "flex", flexDirection: "column", gap: 4,
        }}>
            <span style={{fontSize: 14, color: warn ? "#7A4B00" : "#4A5568", fontWeight: warn ? 600 : 500}}>{label}</span>
            <span style={{fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: 36, lineHeight: 1.1, color: valueColor}}>
                {value}
            </span>
            <span style={{fontSize: 13, color: warn ? "#7A4B00" : "#4A5568"}}>{sub}</span>
        </div>
    );
}

function Segments({bells, size = 10}) {
    return (
        <div style={{display: "flex", gap: 4, width: "100%"}}>
            {bells.map((b, i) => {
                const seg = segColor(b.kind);
                return <span key={i} className={seg.blink ? "dg-next" : ""} style={{flex: 1, height: size, borderRadius: 3, background: seg.bg}} />;
            })}
        </div>
    );
}

function SchoolCard({s, lang, onOpen}) {
    const look = categoryLook(s.cat);
    const name = lang === "ne" && s.nameNe ? s.nameNe : s.name;
    const next = s.next;
    const nextLabel = s.cat === "offline"
        ? `Offline since ${fmtClock(s.offlineSince)}`
        : !next
        ? "All bells done"
        : `Next: ${next.name} · ${fmtClock(next.timeMin)}`;
    const nextColor = s.cat === "offline" ? "#3B4354" : !next ? PALETTE.blueInk : "#7A4B00";
    const last = s.last;
    return (
        <button
            type="button"
            className="dg-card"
            onClick={() => onOpen(s.id)}
            aria-label={`${name}, ${s.address}. ${s.played} of ${s.scheduled} bells rung. ${look.status}.`}
            style={{
                textAlign: "left", cursor: "pointer", background: "#FFFFFF", borderRadius: 18, padding: 18,
                display: "flex", flexDirection: "column", gap: 14, fontFamily: "'Mukta', sans-serif",
                color: PALETTE.ink, border: `1px solid ${look.border}`, borderTop: `4px solid ${look.accent}`,
            }}
        >
            <div style={{display: "flex", alignItems: "flex-start", gap: 12, width: "100%"}}>
                <Logo src={s.logo} initials={s.initials} bg={s.logoBg} />
                <div style={{display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1}}>
                    <span style={{fontWeight: 700, fontSize: 16, lineHeight: 1.25}}>{name}</span>
                    <span style={{fontSize: 13, color: "#4A5568"}}>{s.address}</span>
                </div>
                <span style={{flex: "0 0 auto", padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", background: look.chipBg, color: look.chipFg}}>
                    {look.status}
                </span>
            </div>

            <div style={{display: "flex", alignItems: "flex-end", justifyContent: "space-between", width: "100%"}}>
                <div style={{display: "flex", alignItems: "baseline", gap: 6}}>
                    <span style={{fontFamily: "'Baloo 2', sans-serif", fontWeight: 800, fontSize: 34, lineHeight: 1}}>{s.played}</span>
                    <span style={{fontSize: 15, color: "#4A5568", fontWeight: 600}}>/ {s.scheduled} bells rung</span>
                </div>
                <span style={{fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: 18, color: look.pctColor}}>{s.pct}%</span>
            </div>

            {s.bells.length > 0 ? <Segments bells={s.bells} /> : null}

            <div style={{display: "flex", justifyContent: "space-between", gap: 8, width: "100%", paddingTop: 10, borderTop: "1px solid #EDF1F6", fontSize: 13}}>
                <span style={{color: "#4A5568"}}>Last: <b style={{color: PALETTE.ink}}>{last ? `${last.name} · ${fmtClock(last.timeMin)}` : "—"}</b></span>
                <span style={{fontWeight: 600, color: nextColor}}>{nextLabel}</span>
            </div>
        </button>
    );
}

function Drawer({drawer, lang, onClose, onOpenConsole}) {
    if (!drawer) return null;
    const name = lang === "ne" && drawer.nameNe ? drawer.nameNe : drawer.name;
    const look = categoryLook(drawer.cat);
    return (
        <div style={{position: "fixed", inset: 0, zIndex: 1400, display: "flex", justifyContent: "flex-end"}}>
            <button type="button" aria-label="Close details" onClick={onClose}
                style={{position: "absolute", inset: 0, border: 0, padding: 0, background: "rgba(9,21,43,.45)", cursor: "pointer"}} />
            <aside aria-label="School bell timeline" style={{
                position: "relative", width: 440, maxWidth: "100%", height: "100%", overflowY: "auto",
                background: "#FFFFFF", boxShadow: "-12px 0 32px rgba(9,21,43,.18)", display: "flex", flexDirection: "column",
            }}>
                <div style={{padding: 20, display: "flex", flexDirection: "column", gap: 14, borderBottom: "1px solid #E1E7F0", borderTop: `5px solid ${look.accent}`}}>
                    <div style={{display: "flex", alignItems: "flex-start", gap: 12}}>
                        <Logo src={drawer.logo} initials={drawer.initials} bg={drawer.logoBg} size={56} font={21} />
                        <div style={{flex: 1, display: "flex", flexDirection: "column", gap: 2, minWidth: 0}}>
                            <span style={{fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: 20, lineHeight: 1.2}}>{name}</span>
                            <span style={{fontSize: 14, color: "#4A5568"}}>{drawer.address}</span>
                        </div>
                        <button type="button" className="dg-btn" aria-label="Close" onClick={onClose} style={{
                            width: 44, height: 44, flex: "0 0 44px", borderRadius: 12, border: "1px solid #C3CDDC",
                            background: "#FFFFFF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#09152B" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                                <path d="M6 6l12 12M18 6L6 18" />
                            </svg>
                        </button>
                    </div>
                    <div style={{display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10}}>
                        <div style={{background: "#F3F6FB", borderRadius: 12, padding: "10px 12px", display: "flex", flexDirection: "column"}}>
                            <span style={{fontSize: 12, color: "#4A5568"}}>Rung</span>
                            <span style={{fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: 22}}>{drawer.played} / {drawer.scheduled}</span>
                        </div>
                        <div style={{background: "#F3F6FB", borderRadius: 12, padding: "10px 12px", display: "flex", flexDirection: "column"}}>
                            <span style={{fontSize: 12, color: "#4A5568"}}>Missed</span>
                            <span style={{fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: 22, color: drawer.missColor}}>{drawer.missed}</span>
                        </div>
                        <div style={{background: "#F3F6FB", borderRadius: 12, padding: "10px 12px", display: "flex", flexDirection: "column"}}>
                            <span style={{fontSize: 12, color: "#4A5568"}}>Device</span>
                            <span style={{fontWeight: 700, fontSize: 15, lineHeight: 1.9, color: drawer.devColor}}>{drawer.device}</span>
                        </div>
                    </div>
                    {drawer.bells.length > 0 ? <Segments bells={drawer.bells} /> : null}
                    <button type="button" className="dg-btn" onClick={() => onOpenConsole(drawer.id)} style={{
                        alignSelf: "flex-start", marginTop: 2, padding: "9px 14px", borderRadius: 12, border: `1px solid ${PALETTE.blue}`,
                        background: "#FFFFFF", color: PALETTE.blueInk, fontFamily: "'Mukta', sans-serif", fontWeight: 600, fontSize: 14, cursor: "pointer",
                    }}>
                        View full week console →
                    </button>
                </div>
                <div style={{padding: "16px 20px 28px", display: "flex", flexDirection: "column"}}>
                    <span style={{fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: 17, marginBottom: 10}}>Today's bell timeline</span>
                    {drawer.rows.length === 0 ? (
                        <span style={{fontSize: 14, color: "#4A5568"}}>No bells scheduled for today.</span>
                    ) : drawer.rows.map((b, i) => (
                        <div key={i} style={{display: "flex", alignItems: "stretch", gap: 14}}>
                            <div style={{width: 70, flex: "0 0 70px", textAlign: "right", paddingTop: 9, fontFamily: "'Baloo 2', sans-serif", fontWeight: 600, fontSize: 15, color: b.timeColor}}>
                                {b.time}
                            </div>
                            <div style={{display: "flex", flexDirection: "column", alignItems: "center", width: 18, flex: "0 0 18px"}}>
                                <span className={b.blink ? "dg-next" : ""} style={{marginTop: 13, width: 14, height: 14, borderRadius: "50%", boxSizing: "border-box", background: b.dotBg, border: `2px solid ${b.dotBorder}`}} />
                                {i < drawer.rows.length - 1 ? <span style={{flex: 1, width: 2, background: "#E1E7F0", minHeight: 22}} /> : null}
                            </div>
                            <div style={{flex: 1, padding: "6px 0 14px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8}}>
                                <div style={{display: "flex", flexDirection: "column"}}>
                                    <span style={{fontWeight: 700, fontSize: 15}}>{b.label}</span>
                                    {b.sub ? <span style={{fontSize: 13, color: "#4A5568"}}>{b.sub}</span> : null}
                                </div>
                                <span style={{padding: "2px 9px", borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", background: b.chipBg, color: b.chipFg}}>
                                    {b.state}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </aside>
        </div>
    );
}

/* ── filter pill ───────────────────────────────────────────────────────────── */
function FilterPill({label, count, on, warn, onClick}) {
    return (
        <button type="button" className="dg-btn" aria-pressed={on} onClick={onClick} style={{
            minHeight: 44, padding: "8px 16px", borderRadius: 999, fontFamily: "'Mukta', sans-serif", fontSize: 15,
            fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8,
            border: `1px solid ${on ? PALETTE.ink : "#C3CDDC"}`, background: on ? PALETTE.ink : "#FFFFFF", color: on ? "#FFFFFF" : PALETTE.ink,
        }}>
            {label}
            <span style={{
                minWidth: 22, padding: "0 7px", borderRadius: 999, fontSize: 13, lineHeight: "22px", textAlign: "center",
                background: on ? PALETTE.gold : warn ? "#FDE7DC" : "#E3E9F2", color: on ? PALETTE.ink : warn ? PALETTE.rustInk : PALETTE.ink,
            }}>{count}</span>
        </button>
    );
}

/* ── main view ─────────────────────────────────────────────────────────────── */
function OverallBellStatus({schools: schoolOptions = [], lang = "en", onToggleLang, onPickSchool}) {
    const [schools, setSchools] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [now, setNow] = useState(() => new Date());

    const [filter, setFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState("attention");
    const [selId, setSelId] = useState(null);
    const [drawerBells, setDrawerBells] = useState(null); // hydrated rows for a school w/o inline bells

    const alive = useRef(true);

    const load = useCallback(async () => {
        try {
            const {data} = await fetchOverallBellStatus();
            if (!alive.current) return;
            setSchools(data.schools);
            setSummary(data.summary);
            setError(false);
        } catch (e) {
            if (alive.current) setError(true);
        } finally {
            if (alive.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        alive.current = true;
        load();
        return () => {
            alive.current = false;
        };
    }, [load]);

    // Auto-refresh while the tab is visible.
    useEffect(() => {
        const id = setInterval(() => {
            if (!document.hidden) load();
        }, CONFIG.REFRESH_MS);
        return () => clearInterval(id);
    }, [load]);

    // Keep the live clock ticking.
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 30 * 1000);
        return () => clearInterval(id);
    }, []);

    // Esc closes the drawer.
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && setSelId(null);
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    const counts = useMemo(() => filterCounts(schools), [schools]);
    const list = useMemo(() => filterSort(schools, {filter, query, sort}), [schools, filter, query, sort]);

    const selected = useMemo(() => schools.find((s) => s.id === selId) || null, [schools, selId]);
    const needsHydration = !!selected && !selected.hasBells;

    // When the selected school shipped no inline timeline, hydrate it once from
    // the queue API. Keyed to the school id (not the object) so background polls
    // don't refetch while the drawer is open.
    useEffect(() => {
        setDrawerBells(null);
        if (!selId || !needsHydration) return undefined;
        let keep = true;
        (async () => {
            try {
                const meta = selected ? {name: selected.name, logo: selected.logo, address: selected.address} : {};
                const hydrated = await fetchSchoolQueue(selId, meta);
                if (keep) setDrawerBells(hydrated);
            } catch (e) {
                /* leave the drawer with its summary-only view */
            }
        })();
        return () => {
            keep = false;
        };
    }, [selId, needsHydration]);

    const drawer = useMemo(() => {
        if (!selected) return null;
        const base = drawerBells && !selected.hasBells
            ? {...selected, bells: drawerBells.bells, played: drawerBells.played, missed: drawerBells.missed, scheduled: drawerBells.scheduled || selected.scheduled}
            : selected;
        return buildDrawer(base);
    }, [selected, drawerBells]);

    const neDate = useMemo(() => {
        try {
            return new NepaliDate(now).format("ddd, DD MMMM YYYY", "np");
        } catch (e) {
            return "";
        }
    }, [now]);
    const enDate = now.toLocaleDateString("en-GB", {weekday: "long", day: "numeric", month: "long", year: "numeric"});
    const nowLabel = now.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"});

    const sum = summary || {active: 0, online: 0, rung: 0, scheduled: 0, completed: 0, attention: 0, pct: 0, missPct: 0};

    const filterDefs = [
        ["all", "All schools", false],
        ["live", "In session", false],
        ["done", "Completed", false],
        ["attn", "Needs attention", true],
    ];

    return (
        <div className="dgo" style={{minHeight: "100vh", background: "#F3F6FB"}}>
            <OverallStyles />

            {/* header */}
            <header style={{background: PALETTE.ink, color: "#FFFFFF"}}>
                <div style={{maxWidth: 1360, margin: "0 auto", padding: "14px 24px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 16}}>
                    <div style={{display: "flex", alignItems: "center", gap: 14}}>
                        <div style={{width: 48, height: 48, borderRadius: 12, background: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Baloo 2', sans-serif", fontWeight: 800, color: PALETTE.ink, fontSize: 20}}>
                            घं
                        </div>
                        <div style={{display: "flex", flexDirection: "column"}}>
                            <span style={{fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: 22, lineHeight: 1.1}}>Bell Status</span>
                            <span style={{fontSize: 14, color: "#C9D3E3"}}>घण्टी स्थिति · All active schools today</span>
                        </div>
                    </div>
                    <div style={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12}}>
                        <div style={{display: "flex", flexDirection: "column", gap: 3}}>
                            <span style={{fontSize: 11, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: "#7FA6DE"}}>School</span>
                            <select
                                aria-label="Select a school"
                                className="dg-field"
                                value="all"
                                onChange={(e) => e.target.value !== "all" && onPickSchool && onPickSchool(e.target.value)}
                                style={{
                                    height: 40, minWidth: 210, maxWidth: 260, padding: "0 12px", borderRadius: 10,
                                    border: "1px solid #2B3C5E", background: "#13254A", color: "#FFFFFF",
                                    fontFamily: "'Mukta', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer",
                                }}
                            >
                                <option value="all">All schools (Overall)</option>
                                {schoolOptions.map((s) => (
                                    <option key={s.id} value={s.id} style={{color: "#09152B"}}>
                                        {(lang === "ne" && s.name_ne ? s.name_ne : s.name)}{s.code ? ` · ${s.code}` : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{display: "flex", flexDirection: "column", alignItems: "flex-end"}}>
                            <span style={{fontWeight: 600, fontSize: 15}}>{neDate}</span>
                            <span style={{fontSize: 13, color: "#C9D3E3"}}>{enDate}</span>
                        </div>
                        <div style={{display: "flex", alignItems: "center", gap: 8, background: "#13254A", borderRadius: 999, padding: "8px 14px"}}>
                            <span className="dg-live" style={{width: 10, height: 10, borderRadius: "50%", background: "#2ECC8B"}} />
                            <span style={{fontFamily: "'Baloo 2', sans-serif", fontWeight: 600, fontSize: 16}}>Live · {nowLabel}</span>
                        </div>
                        {onToggleLang ? (
                            <button type="button" className="dg-btn" onClick={onToggleLang} style={{
                                padding: "8px 14px", borderRadius: 999, border: "1px solid #2B3C5E", background: "#13254A",
                                color: "#FFFFFF", fontFamily: "'Mukta', sans-serif", fontWeight: 600, fontSize: 14, cursor: "pointer",
                            }}>
                                {lang === "en" ? "नेपाली" : "English"}
                            </button>
                        ) : null}
                    </div>
                </div>
            </header>

            <main style={{maxWidth: 1360, margin: "0 auto", padding: 24, display: "flex", flexDirection: "column", gap: 20}}>
                {/* summary */}
                <section aria-label="Today's summary" style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16}}>
                    <SummaryCard label="Active schools today" value={sum.active} sub={`${sum.online} devices online`} valueColor={PALETTE.blue} />
                    <SummaryCard label="Bells rung so far"
                        value={<>{sum.rung}<span style={{fontSize: 20, color: "#4A5568", fontWeight: 600}}> / {sum.scheduled}</span></>}
                        sub="scheduled across all schools" valueColor={PALETTE.green} />
                    <SummaryCard label="Completed for the day" value={sum.completed} sub="all bells rung" valueColor={PALETTE.ink} />
                    <SummaryCard label="Needs attention" value={sum.attention} sub="missed bell or device offline" valueColor={PALETTE.rustInk} tone="warn" />
                </section>

                {/* progress */}
                <section aria-label="Overall progress" style={{background: "#FFFFFF", borderRadius: 16, padding: "18px 20px", border: "1px solid #E1E7F0", display: "flex", flexDirection: "column", gap: 10}}>
                    <div style={{display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "baseline", gap: 8}}>
                        <span style={{fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: 18}}>Overall bell progress</span>
                        <span style={{fontSize: 14, color: "#4A5568"}}><b style={{color: PALETTE.ink}}>{sum.pct}%</b> of today's schedule complete</span>
                    </div>
                    <div style={{height: 12, background: "#E3E9F2", borderRadius: 999, overflow: "hidden", display: "flex"}}>
                        <div style={{height: "100%", width: `${sum.pct}%`, background: PALETTE.green}} />
                        <div style={{height: "100%", width: `${sum.missPct}%`, background: PALETTE.rust}} />
                    </div>
                    <div style={{display: "flex", flexWrap: "wrap", gap: 18, fontSize: 13, color: "#4A5568"}}>
                        {[["Rung", PALETTE.green], ["Next bell", PALETTE.gold], ["Missed", PALETTE.rust], ["Scheduled", "#DCE3EE"]].map(([l, c]) => (
                            <span key={l} style={{display: "inline-flex", alignItems: "center", gap: 6}}>
                                <span style={{width: 10, height: 10, borderRadius: 3, background: c, border: l === "Scheduled" ? "1px solid #C3CDDC" : "none"}} />{l}
                            </span>
                        ))}
                    </div>
                </section>

                {/* filters / search / sort / view */}
                <section aria-label="Filters" style={{display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 14}}>
                    <div style={{display: "flex", flexWrap: "wrap", gap: 8}}>
                        {filterDefs.map(([key, label, warn]) => (
                            <FilterPill key={key} label={label} count={counts[key]} warn={warn} on={filter === key} onClick={() => setFilter(key)} />
                        ))}
                    </div>
                    <div style={{display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end"}}>
                        <div style={{display: "flex", flexDirection: "column", gap: 4}}>
                            <label htmlFor="dgo-search" style={{fontSize: 13, fontWeight: 600, color: "#4A5568"}}>Search</label>
                            <div style={{position: "relative"}}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4A5568" strokeWidth="2" strokeLinecap="round" aria-hidden="true" style={{position: "absolute", left: 12, top: 13}}>
                                    <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
                                </svg>
                                <input id="dgo-search" className="dg-field" type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                                    placeholder="School, municipality or district" style={{
                                        width: 280, maxWidth: "100%", height: 44, boxSizing: "border-box", padding: "0 12px 0 38px",
                                        borderRadius: 12, border: "1px solid #C3CDDC", background: "#FFFFFF", fontFamily: "'Mukta', sans-serif", fontSize: 15, color: PALETTE.ink,
                                    }} />
                            </div>
                        </div>
                        <div style={{display: "flex", flexDirection: "column", gap: 4}}>
                            <label htmlFor="dgo-sort" style={{fontSize: 13, fontWeight: 600, color: "#4A5568"}}>Sort by</label>
                            <select id="dgo-sort" className="dg-field" value={sort} onChange={(e) => setSort(e.target.value)} style={{
                                height: 44, padding: "0 12px", borderRadius: 12, border: "1px solid #C3CDDC", background: "#FFFFFF",
                                fontFamily: "'Mukta', sans-serif", fontSize: 15, color: PALETTE.ink,
                            }}>
                                <option value="attention">Needs attention first</option>
                                <option value="progress">Most bells rung</option>
                                <option value="next">Next bell soonest</option>
                                <option value="name">School name (A–Z)</option>
                            </select>
                        </div>
                    </div>
                </section>

                <span style={{fontSize: 14, color: "#4A5568"}}>
                    Showing <b style={{color: PALETTE.ink}}>{list.length}</b> of {sum.active} schools · tap a school to see today's bell timeline
                </span>

                {/* states + grid */}
                {loading && schools.length === 0 ? (
                    <div style={{background: "#FFFFFF", border: "1px solid #E1E7F0", borderRadius: 16, padding: "40px 20px", textAlign: "center", color: "#4A5568", fontSize: 16}}>
                        Loading today's bell status…
                    </div>
                ) : error && schools.length === 0 ? (
                    <div style={{background: "#FFF6E5", border: "1px solid #F5D9A3", borderRadius: 16, padding: "40px 20px", textAlign: "center", color: "#7A4B00", fontSize: 16}}>
                        Could not reach the bell service. Retrying…
                    </div>
                ) : list.length === 0 ? (
                    <div style={{background: "#FFFFFF", border: "1px dashed #C3CDDC", borderRadius: 16, padding: "40px 20px", textAlign: "center", color: "#4A5568", fontSize: 16}}>
                        No schools match this search or filter.
                    </div>
                ) : (
                    <section aria-label="Schools" style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))", gap: 16}}>
                        {list.map((s) => (
                            <SchoolCard key={s.id} s={s} lang={lang} onOpen={setSelId} />
                        ))}
                    </section>
                )}
            </main>

            <Drawer drawer={drawer} lang={lang} onClose={() => setSelId(null)} onOpenConsole={(id) => onPickSchool && onPickSchool(id)} />
        </div>
    );
}

export default OverallBellStatus;
